import { useState, useEffect } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { ReactComponent as WaterRefResetIcon } from './../../img/water_ref_reset.svg';
import { ReactComponent as HardResetIcon } from './../../img/hard_reset.svg';
import { ConfirmationScreenModeChange } from '../confirmationScreenModeChange';
import { ConfirmationModal } from '../ConfirmationModal';
import { useUnitOperations } from './hooks/useUnitOperations';
import { useRfidReader } from '../../hooks/useRfidReader';
import { CONFIRMATION_TYPES, MQTT_ACTIONS, SCREEN_MODES } from './unitSectionConstants';
import { getUnitProperty } from '../../schemas/unitSchema';
import { downloadUnitLog } from '../../services/menderService';


const MqttComponent = ({ selectedUnitItem }) => {
  const [confirmationStates, setConfirmationStates] = useState({
    [CONFIRMATION_TYPES.SCREEN_MODE_CHANGE]: { show: false, confirmed: false },
    [CONFIRMATION_TYPES.WATER_REF_RESET]: { show: false, confirmed: false },
    [CONFIRMATION_TYPES.WATER_FLUSH]: { show: false, confirmed: false },
    [CONFIRMATION_TYPES.SCREEN_RESET]: { show: false, confirmed: false },
    [CONFIRMATION_TYPES.CARTRIDGE_RESET]: { show: false, confirmed: false },
    [CONFIRMATION_TYPES.RESET]: { show: false, confirmed: false },
    [CONFIRMATION_TYPES.LOAD_DEFAULT_CALIBRATION]: { show: false, confirmed: false }
  });

  const [preselectedMode, setPreselectedMode] = useState(null);
  const [sensorSettings, setSensorSettings] = useState(null);
  const [sensorSettingsLoading, setSensorSettingsLoading] = useState(false);
  const [sensorSettingsError, setSensorSettingsError] = useState(null);
  const [showSensorSettingsModal, setShowSensorSettingsModal] = useState(false);

  const [showRfidModal, setShowRfidModal] = useState(false);

  // Get unit ID and center from the selectedUnitItem using the schema helpers
  const unitId = getUnitProperty(selectedUnitItem, 'unit', '');

  const {
    unitsData,
    unitMode,
    fetchUnits,
    executeUnitAction,
    updateUnitMode,
    fetchSensorSettings,
  } = useUnitOperations(unitId);

  const matchingUnit = unitsData.find((unit) => unit.unitID === unitId);
  const deviceSerial = matchingUnit?.devices?.displayID;

  const { uuid: rfidValue, userInfo: rfidUserInfo, reset: resetRfid } = useRfidReader(unitId, showRfidModal);

  // Helper functions to manage confirmation states
  const showConfirmation = (type) => {
    setConfirmationStates(prev => ({
      ...prev,
      [type]: { ...prev[type], show: true }
    }));
  };

  // Read live from selectedUnitItem (kept current by App.js's RTDB listener) rather
  // than useUnitOperations' local state, which is only ever set once from a
  // one-time fetch on mount and goes stale if the device's flag changes afterward.
  const isOutOfOrderActive = getUnitProperty(selectedUnitItem, 'statusInfo._out_of_order_active', false);
  const isManualFlushActive = getUnitProperty(selectedUnitItem, 'statusInfo._water_flush_button_active', false);
  
  const hideConfirmation = (type) => {
    setConfirmationStates(prev => ({
      ...prev,
      [type]: { ...prev[type], show: false }
    }));
  };

  const setConfirmation = (type, confirmed) => {
    setConfirmationStates(prev => ({
      ...prev,
      [type]: { ...prev[type], confirmed }
    }));
  };

  const handleScreenModeChange = (mode) => {
    setPreselectedMode(mode);
    showConfirmation(CONFIRMATION_TYPES.SCREEN_MODE_CHANGE);
  };

  const handleDownloadLog = () => downloadUnitLog(unitId, deviceSerial, 'log.log');

  const handleDownloadPreviousLog = () => downloadUnitLog(unitId, deviceSerial, 'log.log.1');

  const handleDownloadHardwareToml = () => downloadUnitLog(unitId, deviceSerial, 'hardware.toml');

  const handleDownloadUnitConfiguration = () => downloadUnitLog(unitId, deviceSerial, 'configuration/unit_configuration.json');

  const handleGetSensorSettings = async () => {
    setSensorSettings(null);
    setSensorSettingsError(null);
    setSensorSettingsLoading(true);
    setShowSensorSettingsModal(true);
    try {
      const result = await fetchSensorSettings();
      setSensorSettings(result);
    } catch (err) {
      setSensorSettingsError(err.message || 'Failed to retrieve sensor settings.');
    } finally {
      setSensorSettingsLoading(false);
    }
  };

  const handleReadRfid = () => {
    resetRfid();
    setShowRfidModal(true);
  };

  const handleCloseRfidModal = () => {
    setShowRfidModal(false);
    resetRfid();
  };

  // Handle confirmed actions
  useEffect(() => {
    const confirmationActions = {
      [CONFIRMATION_TYPES.SCREEN_MODE_CHANGE]: () => updateUnitMode(preselectedMode),
      [CONFIRMATION_TYPES.WATER_REF_RESET]: () => executeUnitAction(MQTT_ACTIONS.WATER_REF_RESET),
      [CONFIRMATION_TYPES.WATER_FLUSH]: () => executeUnitAction(MQTT_ACTIONS.WATER_FLUSH),
      [CONFIRMATION_TYPES.SCREEN_RESET]: () => executeUnitAction(MQTT_ACTIONS.SCREEN_RESET),
      [CONFIRMATION_TYPES.CARTRIDGE_RESET]: () => executeUnitAction(MQTT_ACTIONS.CARTRIDGE_RESET),
      [CONFIRMATION_TYPES.RESET]: () => executeUnitAction(MQTT_ACTIONS.RESET),
      [CONFIRMATION_TYPES.LOAD_DEFAULT_CALIBRATION]: () => executeUnitAction(MQTT_ACTIONS.LOAD_DEFAULT_CALIBRATION)
    };

    Object.entries(confirmationStates).forEach(([type, state]) => {
      if (state.confirmed && confirmationActions[type]) {
        confirmationActions[type]();
        setConfirmation(type, false);
      }
    });
  }, [
    confirmationStates,
    preselectedMode,
    updateUnitMode,
    executeUnitAction
  ]);

  useEffect(() => {
    fetchUnits();
  }, [fetchUnits]);

  const renderConfirmationModals = () => (
    <>
      {confirmationStates[CONFIRMATION_TYPES.SCREEN_MODE_CHANGE].show && (
        <ConfirmationScreenModeChange
          currentMode={unitMode}
          modeToChange={preselectedMode}
          setConfirmationScreenModeChange={(confirmed) => setConfirmation(CONFIRMATION_TYPES.SCREEN_MODE_CHANGE, confirmed)}
          onShow={confirmationStates[CONFIRMATION_TYPES.SCREEN_MODE_CHANGE].show}
          onClose={() => hideConfirmation(CONFIRMATION_TYPES.SCREEN_MODE_CHANGE)}
        />
      )}
      
      {confirmationStates[CONFIRMATION_TYPES.WATER_REF_RESET].show && (
        <ConfirmationModal
          onShow={confirmationStates[CONFIRMATION_TYPES.WATER_REF_RESET].show}
          onClose={() => hideConfirmation(CONFIRMATION_TYPES.WATER_REF_RESET)}
          onConfirm={(confirmed) => setConfirmation(CONFIRMATION_TYPES.WATER_REF_RESET, confirmed)}
          title="Are you sure you want to reset the water reference of this unit?"
          confirmButtonText="RESET WATER REFERENCE"
          cancelButtonText="Cancel the operation"
        >
          <div className="mt-2 mb-5">
            This action is irreversible and the current reference data will be permanently deleted.
          </div>
        </ConfirmationModal>
      )}

      {confirmationStates[CONFIRMATION_TYPES.WATER_FLUSH].show && (
        <ConfirmationModal
          onShow={confirmationStates[CONFIRMATION_TYPES.WATER_FLUSH].show}
          onClose={() => hideConfirmation(CONFIRMATION_TYPES.WATER_FLUSH)}
          onConfirm={(confirmed) => setConfirmation(CONFIRMATION_TYPES.WATER_FLUSH, confirmed)}
          title="Are you sure you want to activate a water flush in this unit?"
          confirmButtonText="ACTIVATE WATER FLUSH"
          cancelButtonText="Cancel the operation"
        />
      )}

      {confirmationStates[CONFIRMATION_TYPES.SCREEN_RESET].show && (
        <ConfirmationModal
          onShow={confirmationStates[CONFIRMATION_TYPES.SCREEN_RESET].show}
          onClose={() => hideConfirmation(CONFIRMATION_TYPES.SCREEN_RESET)}
          onConfirm={(confirmed) => setConfirmation(CONFIRMATION_TYPES.SCREEN_RESET, confirmed)}
          title="Are you sure you want to reset the screen of this unit?"
          confirmButtonText="RESET SCREEN"
          cancelButtonText="Cancel the operation"
        />
      )}

      {confirmationStates[CONFIRMATION_TYPES.CARTRIDGE_RESET].show && (
        <ConfirmationModal
          onShow={confirmationStates[CONFIRMATION_TYPES.CARTRIDGE_RESET].show}
          onClose={() => hideConfirmation(CONFIRMATION_TYPES.CARTRIDGE_RESET)}
          onConfirm={(confirmed) => setConfirmation(CONFIRMATION_TYPES.CARTRIDGE_RESET, confirmed)}
          title="Are you sure you want to reset the cartridge of this unit?"
          confirmButtonText="RESET CARTRIDGE"
          cancelButtonText="Cancel the operation"
        />
      )}

      {confirmationStates[CONFIRMATION_TYPES.RESET].show && (
        <ConfirmationModal
          onShow={confirmationStates[CONFIRMATION_TYPES.RESET].show}
          onClose={() => hideConfirmation(CONFIRMATION_TYPES.RESET)}
          onConfirm={(confirmed) => setConfirmation(CONFIRMATION_TYPES.RESET, confirmed)}
          title="Are you sure you want to reset this unit?"
          confirmButtonText="RESET"
          cancelButtonText="Cancel the operation"
        />
      )}

      {confirmationStates[CONFIRMATION_TYPES.LOAD_DEFAULT_CALIBRATION].show && (
        <ConfirmationModal
          onShow={confirmationStates[CONFIRMATION_TYPES.LOAD_DEFAULT_CALIBRATION].show}
          onClose={() => hideConfirmation(CONFIRMATION_TYPES.LOAD_DEFAULT_CALIBRATION)}
          onConfirm={(confirmed) => setConfirmation(CONFIRMATION_TYPES.LOAD_DEFAULT_CALIBRATION, confirmed)}
          title="Are you sure you want to load the default calibration for this unit?"
          confirmButtonText="LOAD DEFAULT CALIBRATION"
          cancelButtonText="Cancel the operation"
        >
          <div className="mt-2 mb-5">
            This overwrites the cartridge's current calibration values and resets it to apply them.
          </div>
        </ConfirmationModal>
      )}
    </>
  );

  return (
    <div>
      {renderConfirmationModals()}

      <div className="mt-1">
        <div className='row'>
          <div className=''>
            <Form.Group className="text-start mt-2" controlId="editNotificationTo">
              <Form.Label><strong>SCREEN MODE</strong></Form.Label>
              <div className='pb-3 d-flex justify-content-between'>
                <div className="button-container">
                  <Button
                    variant="success"
                    className={`mqtt-button`}
                    onClick={() => handleScreenModeChange(SCREEN_MODES.MANUAL_FLUSH)}
                    style={{
                      color: isManualFlushActive ? '#25cfc4' : 'black',
                      borderColor: isManualFlushActive ? '#25cfc4' : undefined
                    }}
                  >
                    Manual Flush
                  </Button>
                  <span className="hover-text">Manual Flush Button Mode</span>
                </div>
                <div className="button-container">
                  <Button
                    variant="success"
                    className={`mqtt-button`}
                    onClick={() => handleScreenModeChange(SCREEN_MODES.OUT_OF_ORDER)}
                    style={{
                      color: isOutOfOrderActive ? '#FC664E' : 'black',
                      borderColor: isOutOfOrderActive ? '#FC664E' : undefined
                    }}
                  >
                    Out Of Order
                  </Button>
                  <span className="hover-text">Out Of Order Mode</span>
                </div>
              </div>
            </Form.Group>
          </div>
        </div>
        <div className='row text-start'>
          <Form.Label><strong>ACTIONS</strong></Form.Label>
          <div className='pb-4 d-flex justify-content-between'>
            <div className="button-container">
              <Button
                variant="success"
                className={`mqtt-button`}
                onClick={() => showConfirmation(CONFIRMATION_TYPES.SCREEN_RESET)}
              >
                <img src={require('./../../img/reset_screen.png')} alt="Reset" style={{ width: '30px', height: '30px' }} />
              </Button>
              <span className="hover-text">Screen Reset</span>
            </div>
            <div className="button-container">
              <Button
                variant="success"
                className={`mqtt-button`}
                onClick={() => showConfirmation(CONFIRMATION_TYPES.CARTRIDGE_RESET)}
              >
                <img src={require('./../../img/reset_cartridge.png')} alt="Reset" style={{ width: '30px', height: '30px' }} />
              </Button>
              <span className="hover-text">Cartridge Reset</span>
            </div>
            <div className="button-container">
              <Button
                variant="success"
                className={`mqtt-button`}
                onClick={() => showConfirmation(CONFIRMATION_TYPES.WATER_FLUSH)}
              >
                <img src={require('./../../img/water_flush.png')} alt="Reset" style={{ width: '30px', height: '30px' }} />
              </Button>
              <span className="hover-text">Water Flush</span>
            </div>
            <div className="button-container">
              <Button
                variant="success"
                className={`mqtt-button`}
                onClick={() => showConfirmation(CONFIRMATION_TYPES.WATER_REF_RESET)}
              >
                <WaterRefResetIcon style={{ width: '30px', height: '30px' }} />
              </Button>
              <span className="hover-text">Water Ref Reset</span>
            </div>
          </div>
        </div>
        <div className='row text-start'>
          <div className='pb-4'>
            <div className="button-container">
              <Button
                variant="success"
                className={`mqtt-button`}
                style={{ backgroundColor: '#eb988d', borderColor: '#e05c4b' }}
                onClick={() => showConfirmation(CONFIRMATION_TYPES.RESET)}
              >
                <HardResetIcon style={{ width: '30px', height: '30px' }} />
              </Button>
              <span className="hover-text">Hard Reset</span>
            </div>
          </div>
        </div>
        <div className="col-12 mb-2">
          <Button
            variant="secondary"
            className="mqtt-button w-100"
            style={{ color: 'black' }}
            onClick={handleGetSensorSettings}
          >
            Get Sensor Settings
          </Button>
        </div>
        <div className="col-12 mb-2">
          <Button
            variant="secondary"
            className="mqtt-button w-100"
            style={{ color: 'black' }}
            onClick={() => showConfirmation(CONFIRMATION_TYPES.LOAD_DEFAULT_CALIBRATION)}
          >
            Load Default Calibration
          </Button>
        </div>
        <div className="col-12 mb-2">
          <Button
            variant="secondary"
            className="mqtt-button w-100"
            style={{ color: 'black' }}
            onClick={handleReadRfid}
          >
            Read RFID
          </Button>
        </div>
        <div className="col-12 mb-2">
          <Button
            variant="secondary"
            className="mqtt-button w-100"
            style={{ color: 'black' }}
            onClick={handleDownloadLog}
          >
            Download Scrapp Log
          </Button>
        </div>
        <div className="col-12 mb-4">
          <Button
            variant="secondary"
            className="mqtt-button w-100"
            style={{ color: 'black' }}
            onClick={handleDownloadPreviousLog}
          >
            Download Previous Scrapp Log
          </Button>
        </div>
        <div className="col-12 mb-4">
          <Button
            variant="secondary"
            className="mqtt-button w-100"
            style={{ color: 'black' }}
            onClick={handleDownloadHardwareToml}
          >
            Download Hardware Config
          </Button>
        </div>
        <div className="col-12 mb-4">
          <Button
            variant="secondary"
            className="mqtt-button w-100"
            style={{ color: 'black' }}
            onClick={handleDownloadUnitConfiguration}
          >
            Download Unit Configuration
          </Button>
        </div>
      </div>

      <Modal
        show={showSensorSettingsModal}
        onHide={() => setShowSensorSettingsModal(false)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Sensor Settings — Unit {unitId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {sensorSettingsLoading && (
            <div className="text-center text-muted py-4">
              Waiting for device response…
            </div>
          )}
          {sensorSettingsError && (
            <div className="text-danger">{sensorSettingsError}</div>
          )}
          {sensorSettings && !sensorSettingsLoading && (
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: '0.85rem' }}>
              {JSON.stringify(sensorSettings, null, 2)}
            </pre>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowSensorSettingsModal(false)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showRfidModal}
        onHide={handleCloseRfidModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Read RFID — Unit {unitId}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!rfidValue && (
            <div className="text-center text-muted py-4">
              <Spinner animation="border" size="sm" className="me-2" />
              Waiting for RFID scan…
            </div>
          )}
          {rfidValue && (
            <div className="text-center py-2" style={{ fontSize: '1.1rem', wordBreak: 'break-all' }}>
              {rfidValue}
            </div>
          )}
          {rfidValue && !rfidUserInfo && (
            <div className="text-center text-muted py-2">
              <Spinner animation="border" size="sm" className="me-2" />
              Looking up user…
            </div>
          )}
          {rfidUserInfo && (
            <div className="text-center py-2">
              {rfidUserInfo.userID ? (
                <div>User ID: <strong>{rfidUserInfo.userID}</strong></div>
              ) : (
                <div className="text-danger">No user found for this RFID.</div>
              )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseRfidModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default MqttComponent;